import React from 'react'
import { Tooltip } from './tooltip'
import { useSmartLinkTo } from '../../components/simple/link'
import { Loader } from './loader'

export enum ButtonShape {
  standard = 'text-nowrap px-4 py-[.45rem] min-h-[33.59px] rounded',
  small = 'text-nowrap px-3 py-[.25rem] rounded',
  medium = 'text-nowrap px-3 py-[.325rem] rounded',
  groupStart = 'text-nowrap px-4 py-[.45rem] min-h-[33.59px] rounded-l',
  groupMiddle = 'text-nowrap px-4 py-[.45rem] min-h-[33.59px] rounded-none',
  groupEnd = 'text-nowrap px-4 py-[.45rem] min-h-[33.59px] rounded-r',
  image = 'text-nowrap py-0 pl-0 pr-4 rounded',
  icon = 'p-2 rounded min-h-[30px] w-[30px] max-h-[30px] items-center justify-center',
  iconSmall = 'p-1 rounded min-w-[24.4px] min-h-[24.4px] max-w-[24.4px] max-h-[24.4px] items-center justify-center',
  wideCentered = `text-nowrap w-full flex justify-center ${ButtonShape.standard}`,
  listButton = 'text-nowrap rounded py-2 pl-2 pr-2 flex items-center w-full leading-3 min-h-[30.75px]',
  listButtonThin = 'text-nowrap rounded py-1 pl-2 pr-2 flex items-center w-full leading-3 min-h-[28px]',
  iconCircle = 'p-2 rounded-full items-center justify-center min-w-[40px] min-h-[40px] max-w-[40px] max-h-[40px]',
  iconCircleLarge = 'p-3 rounded-full items-center justify-center min-w-[50px] min-h-[50px] max-w-[50px] max-h-[50px]',
  iconCircleSmall = 'rounded-full items-center justify-center min-w-[24.5px] min-h-[24.5px] max-w-[24.5px] max-h-[24.5px]',
  rounded = 'text-nowrap px-4 py-[.45rem] min-h-[33.59px] rounded-full',
  roundedStart = 'text-nowrap px-4 py-[.45rem] min-h-[33.59px] rounded-l-full',
  roundedMiddle = 'text-nowrap px-4 py-[.45rem] min-h-[33.59px] rounded-none',
  roundedEnd = 'text-nowrap px-4 py-[.45rem] min-h-[33.59px] rounded-r-full',
  roundedMedium = 'text-nowrap px-4 py-[.3rem] rounded-full',
  roundedSmall = 'text-nowrap px-3 py-1 rounded-full text-sm',
  roundedSmallStart = 'text-nowrap px-3 py-1 rounded-l-full text-sm',
  roundedSmallMiddle = 'text-nowrap px-3 py-1 rounded-none text-sm',
  roundedSmallEnd = 'text-nowrap px-3 py-1 rounded-r-full text-sm',
  close = 'rounded-full min-w-[25px] min-h-[25px] max-w-[25px] max-h-[25px] items-center justify-center',
  inlineSmall = '!inline-flex text-nowrap px-3 py-[.25rem] rounded',
}

const focusNone = 'focus:outline-none'
const focusStyles = 'focus:outline-none focus:ring-2 focus:ring-opacity-50'

export enum ButtonStyle {
  logo = `bg-logo-500 hover:bg-logo-600 active:bg-logo-700 text-white focus:ring-logo-400 ${focusStyles}`,
  blue = `bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white focus:ring-blue-400 ${focusStyles}`,
  link = `bg-link-500 hover:bg-link-600 active:bg-link-700 text-white focus:ring-link-400 ${focusStyles}`,
  outlineLink = `border border-link-500 text-link-500 hover:bg-link-500 hover:text-white active:text-white active:bg-link-600 focus:ring-link-400 ${focusStyles}`,
  light = `bg-slate-100 hover:bg-slate-200 active:bg-slate-200 focus:ring-slate-200 ${focusStyles}`,
  blueLight = `bg-grayBlue hover:bg-grayBlue-600 active:bg-grayBlue-600 focus:ring-grayBlue text-charcoal-light ${focusStyles}`,
  charcoal = `bg-charcoal hover:bg-charcoal-hover active:bg-charcoal-active text-charcoal-text focus:ring-charcoal-active ${focusStyles}`,
  charcoalActive = `bg-charcoal-active hover:bg-charcoal-hover active:bg-charcoal-active text-charcoal-text focus:ring-charcoal-active ${focusStyles}`,
  ghost = `hover:bg-slate-100 active:bg-slate-200 focus:ring-slate-200 focus:bg-slate-100 ${focusStyles}`,
  selected = `border-slate-300 bg-slate-200 hover:bg-slate-300 active:bg-slate-300 focus:ring-slate-300 ${focusStyles}`,
  ghostLight = `text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus:ring-slate-100 focus:bg-slate-100 ${focusStyles}`,
  dark = `bg-slate-800 hover:bg-slate-900 active:bg-slate-900 text-white focus:ring-slate-600 ${focusStyles}`,
  outlineBlue = `border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white active:text-white active:bg-blue-600 focus:ring-blue-400 ${focusStyles}`,
  ghostLogoTransparent = `text-logo hover:bg-logo-100 active:bg-logo-200 focus:bg-logo-100 ${focusNone}`,
  ghostGreenTransparent = `text-green-500 hover:bg-green-100 active:bg-green-200 focus:bg-green-100 ${focusNone}`,
  ghostRedTransparent = `text-red-500 hover:bg-red-100 active:bg-red-200 focus:bg-red-100 ${focusNone}`,
  ghostRed = `text-red-500 hover:bg-red-500 hover:text-white active:text-white active:bg-red-600 focus:ring-red-400 ${focusStyles}`,
  ghostLink = `text-link-500 hover:bg-link-500 hover:text-white active:text-white active:bg-link-600 focus:ring-link-400 ${focusStyles}`,
  ghostBlue = `text-blue-500 hover:bg-blue-500 hover:text-white active:text-white active:bg-blue-600 focus:ring-blue-400 ${focusStyles}`,
  ghostLightBlue = `text-charcoal-light border border-transparent hover:border-gray-200 font-semibold hover:bg-gray-100 active:bg-gray-200 focus:ring-gray-100 focus:bg-gray-100 ${focusStyles}`,
  listButton = `hover:bg-charcoal-hover active:bg-charcoal-hover focus:bg-charcoal-hover text-charcoal-text ${focusNone}`,
  ghostWarning = `text-warning-500 hover:bg-warning-500 hover:text-charcoal active:text-charcoal active:bg-warning-600 focus:ring-warning-400 ${focusStyles}`,
  toastClose = `cursor-pointer hover:bg-slate-300/10 active:bg-slate-400/10 focus:ring-slate-400/10 text-slate-900/30 ${focusStyles}`,
  close = `cursor-pointer flex h-7 w-7 items-center justify-center rounded-full text-slate-300 hover:bg-slate-300/20 hover:text-slate-400 focus:ring-slate-100/50 focus:bg-slate-300/20 ${focusStyles}`,
  translucentClose = `cursor-pointer flex h-7 w-7 items-center justify-center rounded-full text-slate-500/25 hover:bg-slate-300/20 hover:text-slate-500/35 focus:ring-slate-100/50 focus:bg-slate-300/20 ${focusStyles}`,
  warning = `bg-warning-500 hover:bg-warning-600 active:bg-warning-700 focus:ring-warning-400 ${focusStyles}`,
  green = `bg-text-500 hover:bg-text-600 active:bg-text-700 focus:ring-text-400 text-white ${focusStyles}`,
  mint = `bg-[#a8c8af] hover:bg-[#a8c8af] active:bg-[#a8c8af] focus:ring-[#a8c8af] text-[#fafafa] ${focusStyles}`,
}

type Elem = HTMLButtonElement | HTMLDivElement | HTMLAnchorElement

export interface ButtonProps extends React.ButtonHTMLAttributes<Elem> {
  /** Additional class names for styling */
  className?: string
  /** The content of the button */
  children: React.ReactNode
  /** Callback function to handle click events */
  onClick?: (event: React.MouseEvent<Elem>) => void
  /** Callback function to handle hover events */
  onHoverChange?: (hovered: boolean) => void
  /** The style of the button (default is light) */
  buttonStyle?: ButtonStyle | string
  /** The shape of the button (default is standard) */
  shape?: ButtonShape
  /** Whether the button is disabled and should not be clickable */
  disabled?: boolean
  /** A tooltip to show when hovering over the button */
  tooltip?: React.ReactNode
  /** Shortcut for buttonStyle={ButtonStyle.logo} */
  logo?: boolean
  /** Shortcut for shape={ButtonShape.small} */
  small?: boolean
  /** Shortcut for shape={ButtonShape.close} and buttonStyle={ButtonStyle.close} */
  close?: boolean
  /** Whether to use a div instead of a button element, which is useful for placing the button inside another interactable element */
  useDivAsButton?: boolean
  /** Whether to show the tooltip on mobile devices */
  tooltipOnMobile?: boolean
  /** Shortcut to make the button into a link */
  link?: string
  /** Whether to open the link in a new tab. Only used if link is set */
  openInNewTab?: boolean
  /** Whether to show a loading spinner */
  showLoader?: boolean
}

/**
 * Button component with various styles, shapes and additional features.
 */
export const Button = React.forwardRef<Elem, ButtonProps>(
  (
    {
      children,
      className,
      buttonStyle = ButtonStyle.light,
      shape = ButtonShape.standard,
      disabled,
      tooltip,
      onClick,
      logo,
      small,
      close,
      onHoverChange,
      useDivAsButton,
      tooltipOnMobile,
      link,
      openInNewTab,
      showLoader,
      ...props
    },
    ref
  ) => {
    if (logo) buttonStyle = ButtonStyle.logo
    if (small) shape = ButtonShape.small
    if (close) {
      buttonStyle = ButtonStyle.close
      shape = ButtonShape.close
    }

    const smartLinkTo = link ? useSmartLinkTo()(link, openInNewTab) : () => {}
    const realOnClick = link
      ? (e: React.MouseEvent<Elem>) => {
          e.preventDefault()
          if (!disabled) smartLinkTo(e)
        }
      : disabled
        ? undefined
        : onClick

    // we cant add pointer-events-none to the button because it will disable the hover effect (needed for tooltips)
    const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : ''

    const buttonProps = {
      ...props,
      ref: ref as React.Ref<HTMLButtonElement>,
      onMouseEnter: (e: React.MouseEvent<Elem>) => {
        if (onHoverChange) onHoverChange(true)
        props.onMouseEnter && props.onMouseEnter(e)
      },
      onMouseLeave: (e: React.MouseEvent<Elem>) => {
        onHoverChange && onHoverChange(false)
        props.onMouseLeave && props.onMouseLeave(e)
      },
      onClick: realOnClick,
      className: `flex items-center gap-2 ease-in-out ${buttonStyle} ${shape} ${disabledClass} ${className || ''}`,
    }

    const content = showLoader ? (
      <>
        {children}
        <Loader small color="text-[#cdd2d880] max-h-1" className="-mr-2 ml-2" />
      </>
    ) : (
      children
    )

    const button = useDivAsButton ? (
      <div {...(buttonProps as React.HTMLAttributes<HTMLDivElement>)}>{content}</div>
    ) : link ? (
      <a
        href={link}
        style={{ textDecoration: 'none' }}
        {...(buttonProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    ) : (
      <button {...(buttonProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}>{content}</button>
    )

    return tooltip ? (
      <Tooltip tooltipOnMobile={tooltipOnMobile} text={tooltip}>
        {button}
      </Tooltip>
    ) : (
      button
    )
  }
)

interface ImageButtonProps extends Omit<ButtonProps, 'shape'> {
  /** The image to display inside the button */
  image: React.ReactNode
}

/**
 * Button component with an image inside on the left
 */
export const ImageButton: React.FC<ImageButtonProps> = ({
  children,
  image,
  className,
  buttonStyle = ButtonStyle.link,
  ...props
}) => {
  return (
    <Button className={`font-semibold ${className}`} shape={ButtonShape.image} buttonStyle={buttonStyle} {...props}>
      <div className="m-[1px] mr-3 flex h-[40px] w-[40px] overflow-hidden rounded-[.2rem] bg-white">{image}</div>
      {children}
    </Button>
  )
}
